import { useRouter } from 'next/router';
import { castArray } from 'lodash';
import {
  Select, MenuItem, Checkbox, FormControl, InputLabel,
} from '@mui/material';
import { NETWORK_DETAILS } from 'lib/constants';
import { updateRouterQuery } from 'lib/utils/filterUtils';

const networkOptions: { [key: string]: string } = {
  twitter: NETWORK_DETAILS.twitter.title,
  instagram: NETWORK_DETAILS.instagram.title,
  facebook: NETWORK_DETAILS.facebook.title,
  youtube: NETWORK_DETAILS.youtube.title,
  tiktok: NETWORK_DETAILS.tiktok.title,
  twitch: NETWORK_DETAILS.twitch.title,
  threads: NETWORK_DETAILS.threads.title,
  linkedin: NETWORK_DETAILS.linkedin.title,
};

const teamNetworkAdditions = {
  snapchat: NETWORK_DETAILS.snapchat.title,
};

interface Props {
  networks?: string[];
  disabled?: boolean;
  includeSnapchat?: boolean;
}

export const NetworkFilter = ({
  networks = [], disabled = false, includeSnapchat = false,
}: Props) => {
  const router = useRouter();
  const networksToUse = includeSnapchat
    ? { ...networkOptions, ...teamNetworkAdditions } : networkOptions;
  const allNetworks = Object.keys(networksToUse);
  const values = networks.length === 0 ? allNetworks : castArray(networks);

  const onChange = (n: string[]) => {
    updateRouterQuery(router, { networks: n });
  };

  const onClose = (): void => {
    // This is a small hack so that the Select blurs when the select menu is closed.
    // See https://stackoverflow.com/questions/65411927/blur-material-ui-select-component-on-the-onchange-event
    setTimeout(() => {
      (document?.activeElement as HTMLInputElement)?.blur();
    }, 0);
  };

  const renderValue = (selected: string[]) => {
    if (selected.length === 1) {
      return networksToUse[selected[0]];
    }

    return `${selected.length} platforms`;
  };

  return (
    <FormControl className="w-full" size="small" disabled={disabled}>
      <InputLabel id="network-select-label">Platforms</InputLabel>
      <Select
        className="w-full"
        label="platforms"
        labelId="network-select-label"
        id="network-select"
        multiple
        onClose={onClose}
        onChange={(e) => onChange(e.target.value as string[])}
        renderValue={renderValue}
        size="small"
        value={values}
      >
        {allNetworks.map(
          (key: string) => (
            <MenuItem key={key} value={key}>
              <div className="flex flex-1 items-center justify-between group">
                <div>
                  <Checkbox size="small" checked={networks.length === 0 || networks.includes(key)} />
                  {networksToUse[key]}
                </div>
                <div className="md:hidden group-hover:block">
                  <button
                    type="button"
                    className="btn-primary-xs"
                    onClick={(e) => {
                      onChange([key]);
                      e.stopPropagation();
                    }}
                  >
                    Only
                  </button>
                </div>
              </div>
            </MenuItem>
          ),
        )}
        <div className="text-right mt-2 pr-2">
          <button
            type="button"
            className="btn-secondary-outlined-xs"
            onClick={() => onChange([])}
            disabled={values.length === allNetworks.length}
          >
            Clear
          </button>
        </div>
      </Select>
    </FormControl>
  );
};
